import { useState, useEffect } from "react";
import {
  Col,
  Flex,
  Row,
  Space,
  Typography,
  Image,
  Form,
  Radio,
  App,
  Upload,
  Alert,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BTN_TXT,
  DESC_TXT,
  GEN_TEXT,
  GLOBAL_CONSTANTS,
  TITLE_TXT,
  INV_TEMP_COL,
  INV_TEMP_DATA,
} from "../../../constants/constants";
import CustomButton from "../../../components/CustomButton/Button";
import InvoiceFormatTable from "./InvoiceFormatTable";
import UploadIcon from "../../../assets/icons/upload-icon.svg";
import SuccessResultModal from "../../../components/SuccessResultModal/SuccessResultModal";
import { CheckCircleFilled } from "@ant-design/icons";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { utils, readFile } from "xlsx";
import {
  formatDateTime,
  handleUploadFile,
  parseUnknownDate,
  fundReqRules,
  resolveMaxFundRequiredPct,
  maxFundAllowedAmount,
} from "../../../helpers/helpers";
import {
  invUpload,
  invValidate,
  updateErrorStatus,
  updateSuccessStatus
} from "../../../redux/slices/InvoiceuploadSlice";
import { DEAL_CATEGORY_IDS, isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";

const { Title, Text } = Typography;

const workSheetName = GEN_TEXT.INVO_FILE;

const UploadFiles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const location = useLocation();
  const { selectedBusEntId, selectedEnt, tenure } = location.state || {};
  const maxFundRequiredPct = resolveMaxFundRequiredPct(selectedEnt?.maxFundRequiredPct);
  const user = useSelector((state) => state?.auth?.user);
  const activeCategory = useSelector((state) => state?.dealCategory?.activeCategory);
  const currentDealCategoryId =
    location.state?.dealCategoryId ||
    activeCategory?.dealCategoryId ||
    user?.dealCategories?.[0]?.dealCategoryId ||
    DEAL_CATEGORY_IDS.INVOICE_FINANCING;
  const isSupplyChain = isSupplyChainCategory(currentDealCategoryId);

  useEffect(() => {
    if (isSupplyChain) {
      navigate("/upload-supply-chain-files", { state: location.state, replace: true });
    }
  }, [isSupplyChain, navigate, location.state]);

  const { success, error, validateLoading, invoiceSubmitLoading } = useSelector(
    (state) => state?.invoiceUpload
  );
  const [financingType, setFinancingType] = useState("LOB1000002");
  const [invoiceData, setInvoiceData] = useState({
    invNum: [],
    invDate: [],
    invDueDate: [],
    invAmt: [],
    totalInvValue: null,
    fundRequired: null,
    invFileUrl: null,
    invZipFileUrl: null,
  });
  const [sheetRows, setSheetRows] = useState([]);
  const [isTempDownloading, setTempDownloading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const workbook = new Excel.Workbook();
  const isSubmitDisabled =
    !invoiceData.totalInvValue ||
    !invoiceData.fundRequired ||
    !invoiceData.invFileUrl ||
    !invoiceData.invZipFileUrl;
  const userEnteredFundReq = form.getFieldValue("fundReq");

  useEffect(() => {
    return () => {
      dispatch(updateErrorStatus());
      dispatch(updateSuccessStatus());
    };
  }, [dispatch]);

  const handleDownloadTemp = async () => {
    setTempDownloading(true);
    try {
      const worksheet = workbook.addWorksheet(workSheetName);
      worksheet.columns = INV_TEMP_COL;
      worksheet.getRow(1).font = { bold: true };

      worksheet.columns.forEach((column) => {
        column.width = column.header.length + 5;
        column.alignment = { horizontal: "center" };
      });

      INV_TEMP_DATA.forEach((row) => worksheet.addRow(row));

      worksheet.eachRow({ includeEmpty: false }, (row) => {
        row._cells.forEach((cell) => {
          worksheet.getCell(cell._address).border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buf = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `${workSheetName}.xlsx`);
    } catch (error) {
      message.error(`Excel Download Error: ${error}`);
    } finally {
      workbook.removeWorksheet(workSheetName);
      setTempDownloading(false);
    }
  };

  const handleUploadChange = (info) => {
    if (info.file.status !== "error") {
      const originalFile = info.file.originFileObj;
      const formattedTime = formatDateTime();
      const newName = `Invoice_${formattedTime}_${originalFile.name}`;

      // update file name in UI
      info.file.name = newName;
    }
  };

  const handleBeforeUpload = (file) => {
    const isExcel =
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";

    if (!isExcel) {
      message.error("You can only upload Excel files!");
      return Upload.LIST_IGNORE;
    }

    const isLt2MB = file.size / 1024 / 1024 < 20;
    if (!isLt2MB) {
      message.error("File must be smaller than 20MB!");
      return Upload.LIST_IGNORE;
    }

    return true; // Allow upload
  };

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    const formattedTime = formatDateTime();
    const safeName = file.name.trim().replace(/\s+/g, "_");
    const renamedFile = new File(
      [file],
      `Invoice_${formattedTime}_${safeName}`,
      {
        type: file.type,
      }
    );


    // Clear old data when new file is uploaded
    setSheetRows([]);

    setInvoiceData((prev) => ({
      ...prev,
      invNum: [],
      invDate: [],
      invDueDate: [],
      invAmt: [],
      totalInvValue: null,
      fundRequired: null,
      invFileUrl: null,
    }));

    form.setFieldsValue({
      totalInvValue: undefined,
      fundReq: undefined,
    });

    const reader = new FileReader();
    reader.onload = (event) => {
      const wb = readFile(event.target.result);
      const sheets = wb.SheetNames;
      const options = { dateNF: "dd-MM-yyyy", raw: false };
      if (sheets.length) {
        const rows = utils.sheet_to_json(wb.Sheets[sheets[0]], options);
        setSheetRows(rows);
      }
    };
    reader.readAsArrayBuffer(file);

    try {
      const path = `invoices/${selectedBusEntId}`;
      const { fileUrl } = await handleUploadFile(renamedFile, path);
      setInvoiceData((prev) => ({
        ...prev,
        invFileUrl: fileUrl,
      }));
      form.setFieldsValue({
        // totalInvValue: 0,
        // fundReq: 0,
      });
      onSuccess("ok");
    } catch (err) {
      console.error("Upload error:", err);
      onError(err);
    }
  };

  const handleRemoveFile = () => {
    setSheetRows([]);

    setInvoiceData((prev) => ({
      ...prev,
      invNum: [],
      invDate: [],
      invDueDate: [],
      invAmt: [],
      totalInvValue: null,
      fundRequired: null,
      invFileUrl: null,
    }));

    form.setFieldsValue({
      totalInvValue: undefined,
      fundReq: undefined,
    });

    dispatch(updateErrorStatus());
    dispatch(updateSuccessStatus());

    return true;
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".xlsx,.xls",
    maxCount: 1,
    beforeUpload: handleBeforeUpload,
    customRequest: handleCustomRequest,
    onChange: handleUploadChange,
    onRemove: handleRemoveFile,
  };

  const handleValidateInvoice = (values) => {
    let invNum = [],
      totInvVal = 0;
    let invAmt = [],
      invDate = [],
      invDueDate = [];

    let hasError = false; // error flag

    const expectedColumns = [
      "Invoice Number",
      "Invoice Date",
      "Invoice Amount",
      "Invoice Due Date",
    ];

    if (sheetRows.length > 0) {
      const uploadedColumns = Object.keys(sheetRows[0] || {});

      const isValidTemplate =
        expectedColumns.length === uploadedColumns.length &&
        expectedColumns.every((col) => uploadedColumns.includes(col));

      if (!isValidTemplate) {
        message.error(
          "Invalid file format. Please upload the downloaded invoice template only."
        );
        return;
      }
    }

    if (sheetRows.length) {
      for (const obj of sheetRows) {
        const formattedObj = {};
        if (!Object.keys(obj).some((k) => obj[k])) {
          message.error("Uploaded file is not valid");
          hasError = true;
          break;
        }

        Object.entries(obj).forEach(([k, v]) => {
          if (k === "Invoice Number") formattedObj.invoiceNumber = v;
          if (k === "Invoice Date") formattedObj.invoiceDate = v;
          if (k === "Invoice Amount") formattedObj.invoiceAmount = v;
          if (k === "Invoice Due Date") formattedObj.invoiceDueDate = v;
        });

        const { invoiceNumber, invoiceAmount, invoiceDate, invoiceDueDate } =
          formattedObj;

        const newinvoiceDate = parseUnknownDate(invoiceDate);
        const newinvoiceDueDate = parseUnknownDate(invoiceDueDate);

        if (new Date(newinvoiceDueDate) < new Date(newinvoiceDate)) {
          message.error("Invoice due date must be greater than invoice date");
          hasError = true;
          break;
        }

        const todayCalendarStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

        if (newinvoiceDate > todayCalendarStr) {
          message.error("Invoice date should not be in the future");
          hasError = true;
          break;
        }

        totInvVal += Number(invoiceAmount);
        invNum.push(invoiceNumber);
        invDate.push(newinvoiceDate);
        invDueDate.push(newinvoiceDueDate);
        invAmt.push(invoiceAmount);
      }

      if (hasError) return; // prevent API call

      const duplicates = invNum.filter((num, idx) => invNum.indexOf(num) !== idx);
      if (duplicates.length > 0) {
        message.error(`Duplicate Invoice numbers found in the uploaded file: ${[...new Set(duplicates)].join(", ")}`);
        return;
      }

      // Auto-calc max fund from credit-limit % (integer math avoids float floor bugs, e.g. 700000*0.7)
      const autoFundReq = maxFundAllowedAmount(totInvVal, maxFundRequiredPct);

      const finalFundReq = autoFundReq;


      form.setFieldsValue({
        totalInvValue: totInvVal,
        fundReq: finalFundReq,
      });

      setInvoiceData((prev) => ({
        ...prev,
        invNum,
        invDate,
        invDueDate,
        invAmt,
        totalInvValue: totInvVal,
        fundRequired: finalFundReq,
      }));

      const payload = {
        busEntId: selectedBusEntId,
        invoiceNumber: invNum,
        invoiceDate: invDate,
        invoiceAmt: invAmt,
        invoiceDueDate: invDueDate,
        fundRequired: finalFundReq,
        dealCategoryId: currentDealCategoryId || DEAL_CATEGORY_IDS.INVOICE_FINANCING,
      };

      dispatch(invValidate(payload));
    }
  };

  const handleInvoicesSubmit = async () => {
    try {
      await form.validateFields(["fundReq"]);

      const fundReq = form.getFieldValue("fundReq");

      const payload = {
        busEntId: selectedBusEntId,
        tenure: Number(tenure),
        totalInvoiceValue: invoiceData.totalInvValue,
        fundRequired: fundReq,
        lineOfBusId: financingType,
        dealCategoryId: currentDealCategoryId || DEAL_CATEGORY_IDS.INVOICE_FINANCING,
        uploadFiles: {
          invoicesummary: {
            invoiceNumber: invoiceData.invNum,
            invoiceDate: invoiceData.invDate,
            invoiceAmt: invoiceData.invAmt,
            invoiceDueDate: invoiceData.invDueDate,
            docUrl: invoiceData.invFileUrl,
          },
          invoiceZipFile: {
            docUrl: invoiceData.invZipFileUrl,
          },
        },
      };

      const result = await dispatch(invUpload(payload));
      if (result.payload.statusCode === 201) {
        setSuccessModalOpen(true)
        setTimeout(() => {
          navigate("/deals");
        }, 2000);
      }
    } catch (error) {
      return;
    }
  };

  return (
    <>
      <Title level={3} style={{ margin: 0, fontSize: 24 }}>
        {TITLE_TXT.INV_UPLOAD}
      </Title>

      <div className="bus-ent-details-container">
        <Row gutter={[0, 16]} justify="space-between">
          <Col>
            <Space direction="vertical" size="middle">
              <Text>
                <strong>{GEN_TEXT.INVO_FOR_ENT}</strong>{" "}
                {selectedEnt?.entName || GLOBAL_CONSTANTS.NA}
              </Text>
              <Text>
                <strong>{GEN_TEXT.INVO_FOR_BUS}</strong>{" "}
                {user?.orgName || GLOBAL_CONSTANTS.NA}
              </Text>
            </Space>
          </Col>
          <Col>
            <Text>
              <strong>{GEN_TEXT.TENURE}</strong>{" "}
              {tenure || GLOBAL_CONSTANTS.NA}
            </Text>
          </Col>
        </Row>
      </div>

      <Row gutter={[0, 16]} style={{ marginTop: 30 }} justify="space-between">
        <Col>
          <Space direction="vertical" size="small">
            <Title level={5} style={{ margin: 0 }}>
              {TITLE_TXT.INV_UPLOAD_STEP_1}
            </Title>
            <Text style={{ color: "#40566D", fontSize: 14 }}>
              {DESC_TXT.INV_UPLOAD_STEP_1}
            </Text>
          </Space>
        </Col>
        <Col>
          <CustomButton
            type="primary"
            style={{ padding: 24 }}
            onClick={handleDownloadTemp}
            loading={isTempDownloading}
          >
            {BTN_TXT.DWNLD_SUM_TEMP}
          </CustomButton>
        </Col>
      </Row>

      <InvoiceFormatTable />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleValidateInvoice}
        initialValues={{
          // totalInvValue: 0,
          // fundReq: 0,
        }}
        className="invoice-upload-form"
        style={{ marginTop: 40 }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item>
                  <Upload {...uploadProps} style={{ width: "100%" }}>
                    <CustomButton
                      color="primary"
                      variant="outlined"
                      block
                      icon={
                        <Image preview={false} src={UploadIcon} alt="upload" />
                      }
                      className="upload-validate-btn"
                    >
                      {BTN_TXT.UPLOAD_SUM_FILE}
                    </CustomButton>
                  </Upload>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item>
                  <CustomButton
                    color="danger"
                    variant="outlined"
                    htmlType="submit"
                    loading={validateLoading}
                    disabled={!invoiceData.invFileUrl}
                    block
                    className="upload-validate-btn"
                  >
                    {BTN_TXT.VALIDATE_SUM}
                  </CustomButton>
                </Form.Item>
              </Col>
            </Row>

          </Col>

          <Col xs={24} lg={12}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <CustomInputNumber
                  label="Total Invoice Value (auto-calculated)"
                  name="totalInvValue"
                  disabled
                  controls={false}
                />
              </Col>
              <Col xs={24} md={12}>
                <CustomInputNumber
                  label="Funding Amount"
                  name="fundReq"
                  controls={false}
                  rules={fundReqRules(invoiceData.totalInvValue || 0, maxFundRequiredPct)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <Row gutter={[0, 16]} style={{ marginTop: 30 }} justify="space-between">
        <Col>
          <Space direction="vertical" size="small">
            <Title level={5} style={{ margin: 0 }}>
              STEP 2 - Upload Invoice Documents (ZIP)
            </Title>
            <Text style={{ color: "#40566D", fontSize: 14 }}>
              Include scanned copies of invoices, viable reports, and proof of
              delivery in a ZIP file (Max size: 20MB).
            </Text>
          </Space>
        </Col>
        <Col>
          <Upload
            name="zipFile"
            accept=".zip"
            maxCount={1}
            className="upload-zip"
            beforeUpload={(file) => {
              const isZip =
                file.type === "application/zip" || file.name.endsWith(".zip");
              if (!isZip) {
                message.error("You can only upload ZIP files!");
                return Upload.LIST_IGNORE;
              }

              const isLt2MB = file.size / 1024 / 1024 < 20;
              if (!isLt2MB) {
                message.error("ZIP file must be smaller than 20MB!");
                return Upload.LIST_IGNORE;
              }

              return true;
            }}
            customRequest={async ({ file, onSuccess, onError }) => {
              const formattedTime = formatDateTime();
              const safeName = file.name.trim().replace(/\s+/g, "_");
              const renamedFile = new File(
                [file],
                `Invoice_${formattedTime}_${safeName}`,
                {
                  type: file.type,
                }
              );
              try {
                const path = `invoices/${selectedBusEntId}`;
                const { fileUrl } = await handleUploadFile(renamedFile, path);
                setInvoiceData((prev) => ({
                  ...prev,
                  invZipFileUrl: fileUrl,
                }));
                onSuccess("ok");
              } catch (error) {
                console.error("ZIP upload failed:", error);
                onError(error);
              }
            }}
            onChange={handleUploadChange}
          >
            <CustomButton
              color="primary"
              variant="outlined"
              block
              icon={<Image preview={false} src={UploadIcon} alt="upload" />}
              style={{ padding: 24 }}
            >
              {BTN_TXT.UPLOAD_DOC_ZIP}
            </CustomButton>
          </Upload>
        </Col>
      </Row>

      <div style={{ marginTop: 30 }}>
        <Space direction="vertical" size="small">
          <Title level={5} style={{ margin: 0 }}>
            STEP 3 - Select Financing Type
          </Title>
          <Text style={{ color: "#40566D", fontSize: 14 }}>
            Choose the type of invoice financing based on your transaction:
          </Text>
        </Space>

        <Radio.Group
          onChange={(e) => setFinancingType(e.target.value)}
          value={financingType}
          className="type-toggle"
        >
          <Radio value="LOB1000002">
            {financingType === "LOB1000002" ? (
              <CheckCircleFilled className="checked-icon" />
            ) : (
              <span className="unchecked-circle" />
            )}
            <span className="label">Receivable</span>
          </Radio>
          <Radio value="LOB1000001" disabled>
            {financingType === "LOB1000001" ? (
              <CheckCircleFilled className="checked-icon" />
            ) : (
              <span className="unchecked-circle" />
            )}
            <span className="label">Payable</span>
          </Radio>
        </Radio.Group>
      </div>

      <div className="sticky-footer">
        <div>
          {(error || success) && (
            <Alert
              message={error || success}
              type={error ? "error" : "success"}
              showIcon
            />
          )}
        </div>
        <Flex gap={10} align="center">
          <CustomButton
            type="default"
            onClick={() => navigate(-1)}
            style={{ padding: 24 }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            type="primary"
            disabled={isSubmitDisabled}
            loading={invoiceSubmitLoading}
            onClick={handleInvoicesSubmit}
            style={{ padding: 24 }}
          >
            {BTN_TXT.SUBMIT}
          </CustomButton>
        </Flex>
      </div>

      <SuccessResultModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title={TITLE_TXT.DOC_SUB_SUC}
        subTitle={DESC_TXT.DOC_SUB_SUC}
      />
    </>
  );
};

export default UploadFiles;
